// ─── Saved Options Builder ────────────────────────────────────────
// Builds the {Option A: 125-only, Option B: 125 + 105 package} payload that
// gets persisted to client.savedOptions and rendered in the Employer Portal
// comparison view.
import {
  getPackageById,
  computeEligibilityBridge,
  budgetToMonthlyAmount,
} from './section105Advisor';
import { optimizeEmployeeWithEligibility } from './calculations';

/**
 * Build the analysed metrics for a client's current Section 125 program.
 * Returns null if no census/config is available.
 */
export function buildSection125Metrics(client, config) {
  const employees = (client && client.employees) || [];
  if (!employees.length || !config) return null;
  let eligible = 0;
  let annualEmployerFica = 0;
  let annualEmployeeBenefit = 0;
  let analyzedCount = 0;
  employees.forEach((e) => {
    const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
    if (!salaryNum) return;
    analyzedCount += 1;
    try {
      const a = optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
      if (a.hasViable && a.optimal) {
        eligible += 1;
        annualEmployerFica += a.optimal.employerAnnualFica || 0;
        annualEmployeeBenefit += (a.optimal.employeeNetMonthly || 0) * 12;
      }
    } catch (err) {
      // skip
    }
  });
  const totalEmployees = analyzedCount;
  const rate = totalEmployees > 0 ? Math.round((eligible / totalEmployees) * 100) : 0;
  const avgMonthly = eligible > 0 ? Math.round(annualEmployeeBenefit / 12 / eligible) : 0;
  return {
    eligibleCount: eligible,
    totalEmployees,
    eligibilityRate: rate,
    annualFicaSavings: Math.round(annualEmployerFica),
    annualEmployeeBenefit: Math.round(annualEmployeeBenefit),
    avgMonthlyBenefit: avgMonthly,
    employerCost: 0,
  };
}

/**
 * Compose a `savedOptions` object with 2 options (A: 125 only, B: 125 + 105 pkg).
 * Caller persists the result on the client record.
 */
export function buildSavedOptions({
  client,
  config,
  packageId,
  monthlyContribution,
  answers,
  createdBy = 'employer_self',
  createdById = null,
}) {
  if (!client) return null;
  const s105Cfg = (config && config.section105Config) || {};
  const monthly = monthlyContribution || budgetToMonthlyAmount(answers?.budget) || 100;
  const pkg = getPackageById(
    packageId,
    s105Cfg.packages,
    s105Cfg.planTypes,
    s105Cfg.coverageLayers
  );
  const baseMetrics = buildSection125Metrics(client, config) || {
    eligibleCount: 0,
    totalEmployees: (client.employees || []).length,
    eligibilityRate: 0,
    annualFicaSavings: 0,
    annualEmployeeBenefit: 0,
    avgMonthlyBenefit: 0,
    employerCost: 0,
  };

  // Option A — Section 125 only (current state)
  const optionA = {
    id: 'option_a',
    label: 'Option A',
    title: 'Section 125 only',
    subtitle: 'Your current program',
    badge: 'current',
    section125: { ...baseMetrics },
    section105: null,
    coverageLayer: null,
    components: [
      { name: 'Section 125 pre-tax', status: 'active' },
      { name: 'Telemedicine', status: 'active' },
      { name: 'Free Rx program', status: 'active' },
      { name: 'Hospitalization insurance', status: 'active' },
    ],
    totalEmployerFica: baseMetrics.annualFicaSavings,
    totalEmployeeCost: 0,
    totalEmployeeValue: baseMetrics.annualEmployeeBenefit,
    netEmployerPosition: baseMetrics.annualFicaSavings,
    healthcareAccess: 'basic',
    complianceStrength: 'good',
  };

  // Option B — recommended package
  const bridge = pkg
    ? computeEligibilityBridge({
        employees: client.employees || [],
        analyzed: (client.employees || []).map((e) => {
          const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
          if (!salaryNum) return e;
          try {
            return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
          } catch (err) {
            return e;
          }
        }),
        config,
        monthlyContribution: monthly,
      })
    : null;

  const newEligibleCount = bridge ? bridge.newEligible : baseMetrics.eligibleCount;
  const newRate = bridge ? Math.round(bridge.newPct * 100) : baseMetrics.eligibilityRate;
  const bridgedCount = bridge ? bridge.bridgedCount : 0;
  const bridgeFica = bridge ? Math.round(bridge.bridgeFicaSavings || 0) : 0;
  const annualHraCost = bridge ? Math.round(bridge.employerAnnualCost || 0) : Math.round(newEligibleCount * monthly * 12);
  const taxDeduction = Math.round(annualHraCost * 0.273); // ~27.3% effective tax shield
  const netEmployerCost = annualHraCost - taxDeduction;
  const coverageMonthly = pkg && pkg._coverage ? Number(pkg._coverage.monthlyCostEstimate || 0) : 0;
  const coverageAnnualCost = coverageMonthly * 12 * newEligibleCount;

  const planComponents = pkg
    ? [
        { name: 'Section 125 pre-tax', status: 'active' },
        { name: 'Telemedicine', status: 'active' },
        { name: 'Free Rx program', status: 'active' },
        { name: 'Hospitalization insurance', status: 'active' },
        { name: `${pkg._planType?.type || 'Section 105'} ($${monthly}/mo)`, status: 'new' },
        ...(pkg._coverage ? [{ name: `${pkg._coverage.name} preventive coverage`, status: 'new' }] : []),
        { name: 'Health coaching', status: 'new' },
        { name: 'DNA + biometric screening', status: 'new' },
      ]
    : [];

  const totalEmployeeValue =
    baseMetrics.annualEmployeeBenefit + annualHraCost + coverageAnnualCost;

  const optionB = pkg
    ? {
        id: 'option_b',
        label: 'Option B',
        title: pkg.name,
        subtitle: pkg.flagged ? 'Recommended — review compliance notes' : 'Recommended — maximum eligibility',
        badge: 'recommended',
        section125: { ...baseMetrics },
        section105: {
          packageId: pkg.id,
          planType: pkg.section105Type,
          monthlyContribution: monthly,
          annualContribution: annualHraCost,
          employerTaxDeduction: taxDeduction,
          netEmployerCost,
          bridgedEmployees: bridgedCount,
          newEligibleCount,
          newEligibilityRate: newRate,
        },
        coverageLayer: pkg._coverage
          ? {
              type: pkg._coverage.id,
              name: pkg._coverage.name,
              monthlyCost: coverageMonthly,
              annualCost: coverageAnnualCost,
              purpose: pkg._coverage.rolesByPackage?.[pkg.id] || pkg._coverage.employerSummary,
            }
          : null,
        components: planComponents,
        totalEmployerFica: baseMetrics.annualFicaSavings + bridgeFica,
        totalEmployeeCost: annualHraCost,
        totalEmployeeValue,
        netEmployerPosition: baseMetrics.annualFicaSavings + bridgeFica - netEmployerCost,
        healthcareAccess: 'full',
        complianceStrength: pkg.flagged ? 'review-required' : 'strong',
        flagged: !!pkg.flagged,
      }
    : null;

  const opts = [optionA];
  if (optionB) opts.push(optionB);

  return {
    createdAt: new Date().toISOString(),
    createdBy,
    createdById,
    status: 'pending',
    selectedOptionId: null,
    selectedAt: null,
    selectedBy: null,
    options: opts,
    sourcePackageId: packageId,
    sourceMonthlyContribution: monthly,
    sourceAnswersHash: hashAnswers(answers),
  };
}

function hashAnswers(answers) {
  if (!answers) return null;
  return Object.entries(answers)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');
}

/**
 * Returns true if the saved options' eligibility totals diverge from the
 * latest census analysis — signals the employer to refresh.
 */
export function savedOptionsAreStale(savedOptions, freshMetrics) {
  if (!savedOptions || !savedOptions.options || !freshMetrics) return false;
  const a = savedOptions.options[0];
  if (!a || !a.section125) return false;
  return (
    a.section125.eligibleCount !== freshMetrics.eligibleCount ||
    a.section125.totalEmployees !== freshMetrics.totalEmployees ||
    a.section125.annualFicaSavings !== freshMetrics.annualFicaSavings
  );
}

/**
 * Has it been more than `days` since the options were saved?
 */
export function savedOptionsAreOlderThan(savedOptions, days = 90) {
  if (!savedOptions || !savedOptions.createdAt) return false;
  const createdMs = new Date(savedOptions.createdAt).getTime();
  if (Number.isNaN(createdMs)) return false;
  return (Date.now() - createdMs) > days * 24 * 60 * 60 * 1000;
}

export const HEALTHCARE_ACCESS_LABELS = { basic: 'Basic', full: 'Full', enhanced: 'Enhanced' };
export const COMPLIANCE_LABELS = {
  good: 'Good',
  strong: 'Strong',
  'review-required': 'Review required',
};
